/**
 * Shared portal theme helpers for editor theme UI.
 * CSS generation stays in sync with app/services/planning/portal_theme_css.rb
 */

export const DEFAULT_THEME = {
  style: 'modern',
  accent_color: '#7c3aed',
  bg_color: '#ffffff',
  text_color: '#1e293b',
  font: 'Inter',
  border_radius: 'md',
  spacing: 'normal',
  custom_css: '',
};

export const STYLE_PRESET_OPTIONS = [
  { value: 'minimal', label: 'Minimal' },
  { value: 'professional', label: 'Professional' },
  { value: 'modern', label: 'Modern' },
  { value: 'classic', label: 'Classic' },
  { value: 'colorful', label: 'Colorful' },
];

/** Full theme snapshots applied when a preset is selected (custom_css preserved by caller). */
export const STYLE_PRESETS = {
  minimal: {
    style: 'minimal',
    accent_color: '#0f172a',
    bg_color: '#ffffff',
    text_color: '#334155',
    font: 'Inter',
    border_radius: 'sm',
    spacing: 'compact',
  },
  professional: {
    style: 'professional',
    accent_color: '#1e40af',
    bg_color: '#f8fafc',
    text_color: '#0f172a',
    font: 'Source Sans 3',
    border_radius: 'md',
    spacing: 'normal',
  },
  modern: {
    style: 'modern',
    accent_color: '#7c3aed',
    bg_color: '#ffffff',
    text_color: '#1e293b',
    font: 'Inter',
    border_radius: 'md',
    spacing: 'normal',
  },
  classic: {
    style: 'classic',
    accent_color: '#92400e',
    bg_color: '#fffbeb',
    text_color: '#292524',
    font: 'Libre Baskerville',
    border_radius: 'none',
    spacing: 'relaxed',
  },
  colorful: {
    style: 'colorful',
    accent_color: '#db2777',
    bg_color: '#fdf4ff',
    text_color: '#3b0764',
    font: 'Poppins',
    border_radius: 'xl',
    spacing: 'normal',
  },
};

export const RADIUS_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' },
];

export const SPACING_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
  { value: 'relaxed', label: 'Relaxed' },
];

/** Legacy keys → display / CSS family names. */
const LEGACY_FONT_MAP = {
  inter: 'Inter',
  serif: 'Georgia',
  mono: 'JetBrains Mono',
};

const RADIUS_MAP = {
  none: '0px',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
};

const SPACING_MAP = {
  compact: '0.75rem',
  normal: '1.5rem',
  relaxed: '2.5rem',
};

const SYSTEM_FONTS = new Set(['Georgia', 'Times New Roman', 'ui-monospace']);

const FALLBACK_METRICS = {
  'Plus Jakarta Sans': {
    size: '103.36%',
    ascent: '103.8%',
    descent: '22.2%',
    lineGap: '0%',
  },
  Inter: {
    size: '107.64%',
    ascent: '90.20%',
    descent: '22.48%',
    lineGap: '0%',
  },
  Poppins: {
    size: '111.11%',
    ascent: '105%',
    descent: '35%',
    lineGap: '10%',
  },
  Roboto: {
    size: '100.06%',
    ascent: '92.77%',
    descent: '24.39%',
    lineGap: '0%',
  },
  'Open Sans': {
    size: '105.55%',
    ascent: '101.18%',
    descent: '27.73%',
    lineGap: '0%',
  },
  Lato: {
    size: '97.39%',
    ascent: '101.62%',
    descent: '22.17%',
    lineGap: '0%',
  },
  Montserrat: {
    size: '112.78%',
    ascent: '96.65%',
    descent: '23.95%',
    lineGap: '0%',
  },
  'Source Sans 3': {
    size: '101.05%',
    ascent: '95.17%',
    descent: '27.32%',
    lineGap: '0%',
  },
  Nunito: {
    size: '101.51%',
    ascent: '101.15%',
    descent: '35.54%',
    lineGap: '0%',
  },
  'DM Sans': {
    size: '106.47%',
    ascent: '98.30%',
    descent: '30.59%',
    lineGap: '0%',
  },
  Manrope: {
    size: '101.68%',
    ascent: '100.91%',
    descent: '28.32%',
    lineGap: '0%',
  },
  Outfit: {
    size: '101.30%',
    ascent: '100%',
    descent: '26%',
    lineGap: '0%',
  },
  Figtree: {
    size: '100%',
    ascent: '100%',
    descent: '26%',
    lineGap: '0%',
  },
  'Work Sans': {
    size: '101.72%',
    ascent: '92.41%',
    descent: '24.58%',
    lineGap: '0%',
  },
};

const DEFAULT_FALLBACK_METRICS = {
  size: '100%',
  ascent: '100%',
  descent: '22%',
  lineGap: '0%',
};

/**
 * Curated Google Fonts for the portal theme picker.
 * Values are stored as family names on theme.font.
 */
export const GOOGLE_FONT_OPTIONS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Source Sans 3',
  'Nunito',
  'Raleway',
  'Work Sans',
  'DM Sans',
  'Manrope',
  'Plus Jakarta Sans',
  'Outfit',
  'Figtree',
  'Space Grotesk',
  'Sora',
  'IBM Plex Sans',
  'Noto Sans',
  'Ubuntu',
  'Rubik',
  'Mulish',
  'Karla',
  'Barlow',
  'Cabin',
  'Josefin Sans',
  'Lexend',
  'Quicksand',
  'Titillium Web',
  'PT Sans',
  'Merriweather',
  'Playfair Display',
  'Lora',
  'Libre Baskerville',
  'Cormorant Garamond',
  'EB Garamond',
  'Crimson Text',
  'Source Serif 4',
  'Spectral',
  'Bitter',
  'Roboto Slab',
  'Oswald',
  'Bebas Neue',
  'Anton',
  'Archivo',
  'Fira Sans',
  'JetBrains Mono',
  'Roboto Mono',
  'Source Code Pro',
  'Space Mono',
  'Georgia',
].map(name => ({ value: name, label: name }));

export const resolveFontFamily = font => {
  if (!font) return 'Inter';
  return LEGACY_FONT_MAP[font] || font;
};

const needsGoogleFont = family => Boolean(family) && !SYSTEM_FONTS.has(family);

export const googleFontHref = theme => {
  const family = resolveFontFamily(theme?.font);
  if (!needsGoogleFont(family)) return null;
  const encoded = encodeURIComponent(family).replace(/%20/g, '+');
  return `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;
};

const fontFamilyCss = family => {
  if (family === 'Georgia') {
    return "'Georgia', 'Times New Roman', serif";
  }
  if (
    ['JetBrains Mono', 'Roboto Mono', 'Source Code Pro', 'Space Mono'].includes(
      family
    )
  ) {
    return `'${family}', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
  }
  return `'${family}', '${family} Fallback', ui-sans-serif, system-ui, sans-serif`;
};

const fallbackFontFaceCss = family => {
  if (!needsGoogleFont(family)) return '';
  const metrics = FALLBACK_METRICS[family] || DEFAULT_FALLBACK_METRICS;
  return `@font-face {
  font-family: '${family} Fallback';
  src: local('Arial'), local('Helvetica Neue'), local('Helvetica');
  size-adjust: ${metrics.size};
  ascent-override: ${metrics.ascent};
  descent-override: ${metrics.descent};
  line-gap-override: ${metrics.lineGap};
}
`;
};

/** Client-side theme CSS for live iframe updates (mirrors PortalThemeCss#to_css). */
export const buildThemeCss = theme => {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  const accent = t.accent_color || DEFAULT_THEME.accent_color;
  const bg = t.bg_color || DEFAULT_THEME.bg_color;
  const text = t.text_color || DEFAULT_THEME.text_color;
  const radius = RADIUS_MAP[t.border_radius] || RADIUS_MAP.md;
  const spacing = SPACING_MAP[t.spacing] || SPACING_MAP.normal;
  const family = resolveFontFamily(t.font);
  const font = fontFamilyCss(family);
  const custom = t.custom_css || '';
  const fallbackFace = fallbackFontFaceCss(family);

  return `${fallbackFace}:root {
  --portal-accent: ${accent};
  --portal-bg: ${bg};
  --portal-text: ${text};
  --portal-text-muted: color-mix(in srgb, ${text} 58%, ${bg});
  --portal-radius: ${radius};
  --portal-spacing: ${spacing};
  --accent: ${accent};
  --bg: ${bg};
  --text: ${text};
  --radius: ${radius};
  --spacing: ${spacing};
}
body {
  font-family: ${font};
  background-color: var(--portal-bg);
  color: var(--portal-text);
  margin: 0;
  min-height: 100vh;
}
[data-portal-tab][hidden] { display: none !important; }
[data-portal-tabs-nav] { min-height: 2.25rem; }
a, .accent, .portal-accent {
  color: var(--portal-accent);
}
.portal-accent-soft {
  background-color: color-mix(in srgb, var(--portal-accent) 12%, white);
  color: var(--portal-accent);
}
.portal-icon-well {
  background-color: color-mix(in srgb, var(--portal-accent) 12%, white);
  color: var(--portal-accent);
}
.portal-btn,
button.portal-btn,
a.portal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: var(--portal-accent);
  color: #fff;
  border-radius: var(--portal-radius);
  padding: 0.625rem 1.5rem;
  border: none;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.portal-btn:hover,
a.portal-btn:hover {
  filter: brightness(0.92);
  color: #fff;
}
.portal-btn-outline,
a.portal-btn-outline {
  display: inline-block;
  background-color: transparent;
  color: var(--portal-accent);
  border: 2px solid var(--portal-accent);
  border-radius: var(--portal-radius);
  padding: 0.625rem 1.5rem;
  font-weight: 500;
  text-decoration: none;
}
.portal-btn-outline:hover,
a.portal-btn-outline:hover {
  background-color: color-mix(in srgb, var(--portal-accent) 8%, white);
}
.portal-card,
.card,
.panel {
  border-radius: var(--portal-radius);
}
.portal-surface {
  background-color: var(--portal-bg);
  color: var(--portal-text);
}
.portal-tab-btn { color: var(--portal-text-muted); }
.portal-tab-btn[aria-selected="true"] {
  color: var(--portal-accent);
  background-color: color-mix(in srgb, var(--portal-accent) 12%, white);
}
.text-slate-950, .text-slate-900, .text-slate-800, .text-slate-700,
.text-gray-950, .text-gray-900, .text-gray-800, .text-gray-700,
.text-zinc-950, .text-zinc-900, .text-zinc-800, .text-zinc-700,
.text-neutral-950, .text-neutral-900, .text-neutral-800, .text-neutral-700,
.prose, .prose-slate {
  color: var(--portal-text) !important;
}
.text-slate-600, .text-slate-500, .text-slate-400, .text-slate-300,
.text-gray-600, .text-gray-500, .text-gray-400,
.text-zinc-600, .text-zinc-500, .text-zinc-400,
.text-neutral-600, .text-neutral-500, .text-neutral-400 {
  color: var(--portal-text-muted) !important;
}
[data-portal-fg] :where(h1, h2, h3, h4, h5, h6, p, span, li, td, th, label, div, a):not(.portal-btn):not(.portal-btn-outline):not(.portal-icon-well):not(.portal-accent):not(.portal-accent-soft) {
  color: inherit !important;
}
.text-violet-700, .text-violet-600, a.text-violet-700, a.text-violet-600 {
  color: var(--portal-accent) !important;
}
.bg-violet-600, .bg-violet-700 {
  background-color: var(--portal-accent) !important;
}
.bg-violet-100, .bg-violet-50 {
  background-color: color-mix(in srgb, var(--portal-accent) 12%, white) !important;
}
.border-violet-700, .border-violet-600 {
  border-color: var(--portal-accent) !important;
}
${custom}
`;
};

export const applyPreset = (theme, presetKey) => {
  const preset = STYLE_PRESETS[presetKey];
  if (!preset) return { ...theme, style: presetKey };
  return {
    ...theme,
    ...preset,
    custom_css: theme.custom_css || '',
  };
};

export default {
  DEFAULT_THEME,
  STYLE_PRESETS,
  STYLE_PRESET_OPTIONS,
  GOOGLE_FONT_OPTIONS,
  applyPreset,
  resolveFontFamily,
  buildThemeCss,
  googleFontHref,
};
